import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { PipelineStep, PipelineSourceField, ConfigValue } from './types';

interface LivePreviewStripProps {
  sourceField: PipelineSourceField | undefined;
  steps: PipelineStep[];
  sampleData?: Record<string, Array<Record<string, unknown>>>;
}

/** Lightweight client-side evaluator for simple transforms. Returns null if cannot evaluate. */
const evaluateLocally = (
  value: unknown,
  steps: PipelineStep[],
  sourceFields: PipelineSourceField[],
  sampleRow?: Record<string, unknown>
): string | null => {
  if (value === null || value === undefined) return null;
  let current = String(value);

  for (const step of steps) {
    const result = applyStep(current, step, sourceFields, sampleRow);
    if (result === null) return null;
    current = result;
  }

  return current;
};

const resolveConfigValue = (
  cv: ConfigValue,
  sourceFields: PipelineSourceField[],
  sampleRow?: Record<string, unknown>
): string | null => {
  if (cv.kind === 'literal') return cv.value;
  if (cv.kind === 'field') {
    const sf = sourceFields.find((f) => f.id === cv.fieldId);
    const fieldName = sf?.sourceField ?? cv.fieldName;
    if (sampleRow && fieldName in sampleRow) {
      const val = sampleRow[fieldName];
      return val === null || val === undefined ? '' : String(val);
    }
    return null;
  }
  return null;
};

const applyStep = (
  input: string,
  step: PipelineStep,
  sourceFields: PipelineSourceField[],
  sampleRow?: Record<string, unknown>
): string | null => {
  const c = step.config;
  switch (step.functionName) {
    case 'UPPER':
      return input.toUpperCase();
    case 'LOWER':
      return input.toLowerCase();
    case 'TRIM':
      return input.trim();
    case 'ABS': {
      const num = parseFloat(input);
      return Number.isNaN(num) ? null : String(Math.abs(num));
    }
    case 'ROUND': {
      const num = parseFloat(input);
      if (Number.isNaN(num)) return null;
      return num.toFixed(c.decimals ?? 2);
    }
    case 'REPLACE':
      if (!c.find) return input;
      return input.split(c.find).join(c.replaceWith ?? '');
    case 'SPLIT': {
      const parts = input.split(c.delimiter ?? ',');
      const idx = c.index ?? 0;
      return idx >= 0 && idx < parts.length ? parts[idx].trim() : null;
    }
    case 'CONCAT': {
      if (!c.parts || c.parts.length === 0) return null;
      const resolved = c.parts.map((p) => resolveConfigValue(p, sourceFields, sampleRow));
      if (resolved.some((r) => r === null)) return null;
      return resolved.join('');
    }
    case 'COALESCE': {
      if (!c.fallbacks || c.fallbacks.length === 0) return null;
      for (const fb of c.fallbacks) {
        const val = resolveConfigValue(fb, sourceFields, sampleRow);
        if (val !== null && val !== '') return val;
      }
      return '';
    }
    case 'IS_NULL':
      return (input === '' || input === 'null' || input === 'undefined') ? 'true' : 'false';
    case 'IS_NOT_NULL':
      return (input === '' || input === 'null' || input === 'undefined') ? 'false' : 'true';
    case 'LENGTH':
      return String(input.length);
    case 'LITERAL':
      return c.literalValue ?? '';
    case 'TO_DATE':
    case 'TO_TIMESTAMP':
    case 'PARSE_DATE':
      // Can't actually parse dates client-side reliably, show placeholder
      return '(date)';
    case 'TO_CHAR':
    case 'DATE_FORMAT':
      return '(formatted)';
    case 'SUBSTRING': {
      const start = (c.start ?? 1) - 1; // SQL is 1-based
      const len = c.length ?? 10;
      return input.substring(start, start + len);
    }
    case 'LEFT':
      return input.substring(0, c.length ?? 5);
    case 'RIGHT':
      return input.substring(Math.max(0, input.length - (c.length ?? 5)));
    case 'LPAD': {
      const targetLen = c.padLength ?? 10;
      const padCh = c.padChar ?? '0';
      return input.length >= targetLen ? input : input.padStart(targetLen, padCh);
    }
    case 'RPAD': {
      const targetLen = c.padLength ?? 10;
      const padCh = c.padChar ?? ' ';
      return input.length >= targetLen ? input : input.padEnd(targetLen, padCh);
    }
    case 'NVL': {
      if (input && input !== '' && input !== 'null') return input;
      if (!c.fallbacks || c.fallbacks.length === 0) return '';
      const val = resolveConfigValue(c.fallbacks[0], sourceFields, sampleRow);
      return val ?? '';
    }
    case 'CAST':
      return input; // Just pass through for preview
    case 'CASE_WHEN': {
      const branches = c.branches ?? [];
      for (const b of branches) {
        if (input === b.condition || input.toLowerCase() === b.condition.toLowerCase()) {
          return b.result;
        }
      }
      return c.elseResult ?? input;
    }
    default:
      // Complex functions — can't evaluate client-side
      return null;
  }
};

const LivePreviewStrip: React.FC<LivePreviewStripProps> = ({
  sourceField,
  steps,
  sampleData,
}) => {
  const preview = useMemo(() => {
    if (!sourceField) return null;

    // Try to find a sample value from sampleData
    let sampleValue: unknown = null;
    let sampleRow: Record<string, unknown> | undefined;

    if (sampleData) {
      // Look through all tables to find the source field
      for (const rows of Object.values(sampleData)) {
        if (Array.isArray(rows) && rows.length > 0) {
          const row = rows[0];
          if (row && typeof row === 'object' && sourceField.sourceField in row) {
            sampleValue = (row as Record<string, unknown>)[sourceField.sourceField];
            sampleRow = row as Record<string, unknown>;
            break;
          }
        }
      }
    }

    // If no sample data, use a placeholder based on field name heuristics
    if (sampleValue === null || sampleValue === undefined) {
      const fieldLower = sourceField.sourceField.toLowerCase();
      if (sourceField.sourceField === 'email' || fieldLower.includes('email')) {
        sampleValue = 'user@example.com';
      } else if (fieldLower.includes('phone')) {
        sampleValue = '+1-555-0123';
      } else if (fieldLower.includes('address')) {
        sampleValue = '123 Main St';
      } else if (fieldLower.includes('name')) {
        sampleValue = 'John Doe';
      } else if (fieldLower.includes('date')) {
        sampleValue = '2024-01-15';
      } else if (fieldLower.includes('amount') || fieldLower.includes('price') || fieldLower.includes('cost')) {
        sampleValue = '1234.567';
      } else if (fieldLower.includes('id')) {
        sampleValue = '12345';
      } else if (fieldLower.includes('status')) {
        sampleValue = 'active';
      } else if (fieldLower.includes('code') || fieldLower.includes('zip')) {
        sampleValue = '10001';
      } else if (fieldLower.includes('country')) {
        sampleValue = 'US';
      } else {
        sampleValue = 'sample_value';
      }
    }

    if (steps.length === 0) {
      return {
        input: String(sampleValue),
        output: String(sampleValue),
        evaluated: true,
      };
    }

    const output = evaluateLocally(sampleValue, steps, [sourceField], sampleRow);
    return {
      input: String(sampleValue),
      output: output ?? '(save to preview)',
      evaluated: output !== null,
    };
  }, [sourceField, steps, sampleData]);

  if (!preview) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.5,
        py: 0.75,
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, flexShrink: 0 }}
      >
        Preview
      </Typography>

      <Box
        sx={{
          px: 1,
          py: 0.25,
          bgcolor: 'background.paper',
          borderRadius: 0.5,
          border: '1px solid',
          borderColor: 'success.light',
          fontFamily: 'monospace',
          fontSize: '0.72rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 140,
          color: 'success.dark',
        }}
      >
        {preview.input}
      </Box>

      <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />

      <Box
        sx={{
          px: 1,
          py: 0.25,
          bgcolor: preview.evaluated ? 'background.paper' : 'warning.50',
          borderRadius: 0.5,
          border: '1px solid',
          borderColor: preview.evaluated ? 'secondary.light' : 'warning.light',
          fontFamily: 'monospace',
          fontSize: '0.72rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 140,
          color: preview.evaluated ? 'secondary.dark' : 'warning.dark',
          fontStyle: preview.evaluated ? 'normal' : 'italic',
        }}
      >
        {preview.output}
      </Box>
    </Stack>
  );
};

export default LivePreviewStrip;
